import type { Command } from "../document/commands.ts";
import type { MapDocument, WorldDocument } from "../document/types.ts";
import { areaReach, shiftedRect, type AreaOffset, type AreaReach, type AreaReachKind, type AreaRect } from "./area.ts";
import { deleteArea, moveArea, pasteArea, type AreaCopyOutcome, type AreaCutOutcome, type AreaDeleteOutcome, type AreaDuplicateOutcome, type AreaMoveOutcome, type AreaPasteOutcome, type AreaPasteStart } from "./area-actions.ts";
import { areaClipboardNotes, areaDoorCells, copyArea, type AreaClipboard, type AreaClipboardNotes } from "./area-clipboard.ts";
import type { AreaContents, PropExtentOf } from "./area-contents.ts";
import type { AreaDeleteGuard } from "./area-delete.ts";
import type { AreaTileBox } from "./area-replaced.ts";
import type { AreaMovePlan } from "./area-move.ts";
import { AreaPending } from "./area-pending.ts";
import { planAreaPaste, type AreaPastePlan } from "./area-paste.ts";
import { AreaPlans, sameAreaRect } from "./area-plans.ts";
import { commandStands } from "./area-turn.ts";

/**
 * The area's view state (ADR-128 sections A to C2, decisions 33 and 41c), through `EditorStore.area`: the select
 * tool's reach, the rectangle of whole tiles an `all` selection holds and the rectangle a drag is drawing (a
 * selection or, with Shift, an erase box). It composes the pending move (`area-pending.ts`), the counts and plans
 * the planner works out for it (`area-plans.ts`) and the two edits (`area-actions.ts`): Delete and the box erase,
 * and a confirmed move, after which the selection stands at the destination (decision 28) and goes back to the
 * source with the content when that move is undone. Copy, cut, paste and duplicate (decisions 37 to 39) keep the
 * clipboard here: a paste is a pending block that reuses the move's offset, carry, drag and step with the clipboard's
 * rectangle as its source, and is confirmed by `confirmPaste`. Nothing here is saved, sent or undone: selecting and
 * placing run no command (decision 15). The base is never taken: "Include the base" (decision 3d) is not offered, so
 * `includeBase` is always off.
 */

/**
 * What the slice needs from the store: the document and the map in view, the active layer, each layer's lock and
 * eye (its own or its group's), what the delete's reference check reads, the store's one way to run a command and
 * its notification.
 */
export interface AreaHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly mapId: () => string;
  readonly activeLayerId: () => string | null;
  readonly isLocked: (layerId: string) => boolean;
  readonly isHidden: (layerId: string) => boolean;
  readonly guard: () => AreaDeleteGuard;
  readonly allocateIds: (prefix: string, count: number) => string[];
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * A rectangle a drag is drawing: one that selects on release, or one that erases (Shift at the press).
 */
export interface AreaDraft {
  readonly rect: AreaRect;
  readonly erase: boolean;
}

/**
 * A rectangle held to a map: its corners clamped to the map's tiles.
 *
 * @param map The map.
 * @param rect The rectangle, corners in order.
 * @returns The rectangle inside the map.
 */
function heldToMap(map: MapDocument, rect: AreaRect): AreaRect {
  /**
   * A whole number held between 0 and a most.
   *
   * @param value The number.
   * @param most The most.
   * @returns The held number.
   */
  const hold = (value: number, most: number): number => Math.max(0, Math.min(most, Math.round(value)));
  const x0 = hold(Math.min(rect.x0, rect.x1), map.width - 1);
  const x1 = hold(Math.max(rect.x0, rect.x1), map.width - 1);
  const y0 = hold(Math.min(rect.y0, rect.y1), map.height - 1);
  const y1 = hold(Math.max(rect.y0, rect.y1), map.height - 1);
  return { x0, y0, x1, y1 };
}

/**
 * What callers reach of the area, through `EditorStore.area`.
 */
export type AreaSlice = Pick<
  AreaEdits,
  "reach" | "setReach" | "rect" | "select" | "selectAll" | "clear" | "draft" | "setDraft" | "shown" | "showsOverlays" | "layers" | "contents" | "bindExtent" | "delete" | "erase" | "offset" | "carried" | "destination" | "centre" | "clipboard" | "clipboardNotes" | "pasting" | "pendingSource" | "pendingPlan" | "copy" | "cut" | "paste" | "duplicate" | "confirmPaste" | "mapCentre" | "pickUp" | "carryTo" | "putDown" | "dragTo" | "setOffset" | "nudge" | "cancelMove" | "movePlan" | "replacedBoxes" | "move"
>;

/**
 * The area's view state.
 */
export class AreaEdits {
  private reachKind: AreaReachKind = "layer";
  private selected: AreaRect | null = null;
  private drawing: AreaDraft | null = null;
  private lastMove: { readonly command: Command; readonly from: AreaRect; readonly to: AreaRect } | null = null;
  private readonly plans = new AreaPlans();
  private readonly pending: AreaPending;
  private held: AreaClipboard | null = null;
  private pasteBase: AreaRect | null = null;
  private keptPaste: { readonly world: WorldDocument; readonly held: AreaClipboard; readonly offset: AreaOffset; readonly key: string; readonly plan: AreaPastePlan } | null = null;
  private keptContents: { readonly map: MapDocument; readonly rect: AreaRect; readonly contents: AreaContents } | null = null;
  private keptPlan: { readonly world: WorldDocument; readonly rect: AreaRect; readonly offset: AreaOffset; readonly plan: AreaMovePlan } | null = null;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: AreaHost) {
    this.pending = new AreaPending({ rect: () => this.pasteBase ?? this.selected, map: () => host.map(), notify: () => host.notify() });
  }

  /**
   * Forgets the counts and the plan read last, so the next reading asks the planner's cache again with the reach
   * as it then stands. The store calls this on every notification: a lock or an eye changes the reach without
   * changing the document. Between two notifications a reading for the same document, rectangle and offset is one
   * comparison of identities, which is what lets the map ask for the plan every frame.
   */
  public invalidate(): void {
    this.keptContents = null;
    this.keptPlan = null;
  }

  /**
   * The select tool's reach (decision 1): the active layer alone, which is every selection the tool makes without
   * this slice, or every visible, unlocked layer. A new store starts on `layer`.
   *
   * @returns The reach.
   */
  public get reach(): AreaReachKind {
    return this.reachKind;
  }

  /**
   * Sets the reach. Going back to `layer` drops the rectangle, the one being drawn and a pending move.
   *
   * @param reach The reach.
   */
  public setReach(reach: AreaReachKind): void {
    if (reach === this.reachKind) {
      return;
    }
    this.reachKind = reach;
    if (reach === "layer") {
      this.selected = null;
      this.drawing = null;
      this.endPending();
    }
    this.host.notify();
  }

  /**
   * The selected rectangle of whole tiles, both corners inclusive, or null. Held only with the `all` reach.
   *
   * @returns The rectangle.
   */
  public get rect(): AreaRect | null {
    return this.selected;
  }

  /**
   * Selects a rectangle, held to the map, or nothing, and ends the rectangle being drawn; another rectangle puts a
   * pending move back. With the `layer` reach it selects nothing.
   *
   * @param rect The rectangle, or null.
   */
  public select(rect: AreaRect | null): void {
    const next = rect === null || this.reachKind !== "all" ? null : heldToMap(this.host.map(), rect);
    if (sameAreaRect(next, this.selected) && this.drawing === null) {
      return;
    }
    if (!sameAreaRect(next, this.selected)) {
      this.endPending();
    }
    this.selected = next;
    this.drawing = null;
    this.host.notify();
  }

  /**
   * Selects the whole map (decision 8), with the `all` reach.
   *
   * @returns False with the `layer` reach, which this slice selects nothing for.
   */
  public selectAll(): boolean {
    if (this.reachKind !== "all") {
      return false;
    }
    const map = this.host.map();
    this.select({ x0: 0, y0: 0, x1: map.width - 1, y1: map.height - 1 });
    return true;
  }

  /**
   * Drops the rectangle, the one being drawn and a pending move or paste; the reach stays.
   */
  public clear(): void {
    if (this.selected === null && this.drawing === null && this.pasteBase === null) {
      return;
    }
    this.selected = null;
    this.drawing = null;
    this.endPending();
    this.host.notify();
  }

  /**
   * The rectangle a drag is drawing, or null.
   *
   * @returns The draft.
   */
  public get draft(): AreaDraft | null {
    return this.drawing;
  }

  /**
   * Sets the rectangle a drag is drawing, held to the map, or ends it. Subscribers hear of it only when its cells
   * or its kind changed, so a pointer moving inside one tile costs nothing. With the `layer` reach nothing is drawn.
   *
   * @param draft The draft, or null.
   */
  public setDraft(draft: AreaDraft | null): void {
    const next = draft === null || this.reachKind !== "all" ? null : { rect: heldToMap(this.host.map(), draft.rect), erase: draft.erase };
    if (sameAreaRect(next?.rect ?? null, this.drawing?.rect ?? null) && next?.erase === this.drawing?.erase) {
      return;
    }
    this.drawing = next;
    this.host.notify();
  }

  /**
   * The rectangle the bar describes: the one being drawn while a drag runs, else the selected one.
   *
   * @returns The rectangle, or null.
   */
  public get shown(): AreaRect | null {
    return this.drawing?.rect ?? this.selected;
  }

  /**
   * Whether the map shows the collision and zone overlays whatever their switches say (decision 41c): while a
   * rectangle stands or is drawn, so nothing a Delete or a box erase clears goes unseen.
   *
   * @returns True while forced.
   */
  public get showsOverlays(): boolean {
    return this.shown !== null;
  }

  /**
   * The layers the reach takes and leaves on the map in view, as the locks and eyes stand now.
   *
   * @returns The reach.
   */
  public layers(): AreaReach {
    const map = this.host.map();
    return areaReach(map, { reach: this.reachKind, activeLayerId: this.host.activeLayerId(), locked: map.layers.filter((l) => this.host.isLocked(l.id)).map((l) => l.id), hidden: map.layers.filter((l) => this.host.isHidden(l.id)).map((l) => l.id), includeBase: false });
  }

  /**
   * Binds each prop's extent on the map, which says whether the rectangle's edge cuts it (decision 11). Unbound, no
   * prop is counted as cut.
   *
   * @param extentOf The extent lookup.
   */
  public bindExtent(extentOf: PropExtentOf): void {
    this.plans.bindExtent(extentOf);
    this.invalidate();
  }

  /**
   * What the shown rectangle holds (`areaContents`), counted once for a rectangle, a document and a reach.
   *
   * @returns The contents, or null with no rectangle.
   */
  public contents(): AreaContents | null {
    const rect = this.shown;
    if (rect === null) {
      return null;
    }
    const map = this.host.map();
    const kept = this.keptContents;
    if (kept !== null && kept.map === map && kept.rect === rect) {
      return kept.contents;
    }
    const contents = this.plans.contents(map, rect, this.layers());
    this.keptContents = { map, rect, contents };
    return contents;
  }

  /**
   * Delete over the selected rectangle (decision 33): everything the reach takes inside it, as one compound. The
   * rectangle stays selected. While a move of the area is pending nothing is deleted: the builder confirms the
   * move or puts it back first, so Delete never empties a place the outline has already left.
   *
   * @returns What it did.
   */
  public delete(): AreaDeleteOutcome {
    if (this.selected === null) {
      return { kind: "nothing" };
    }
    if (this.pending.offset !== null) {
      return { kind: "moving" };
    }
    return deleteArea(this.host, this.selected, this.layers().taken, this.host.guard());
  }

  /**
   * The box erase's release (decision 41c): clears the erase box being drawn as one compound and ends the draft,
   * whatever the outcome, a failure of the planner included. A draft that selects erases nothing and is kept.
   *
   * @returns What it did.
   * @throws Error when the planner does; the draft is ended and subscribers are told first.
   */
  public erase(): AreaDeleteOutcome {
    const draft = this.drawing;
    if (draft === null || !draft.erase) {
      return { kind: "nothing" };
    }
    this.drawing = null;
    let ran = false;
    try {
      const outcome = deleteArea(this.host, draft.rect, this.layers().taken, this.host.guard());
      ran = outcome.kind === "deleted";
      return outcome;
    } finally {
      if (!ran) {
        this.host.notify();
      }
    }
  }

  /**
   * The pending move's offset in whole tiles (decision 16), or null while none is pending. The map is unchanged
   * until `move` confirms it.
   *
   * @returns The offset.
   */
  public get offset(): AreaOffset | null {
    return this.pending.offset;
  }

  /**
   * Whether the block is picked up and follows the pointer (decision 51).
   *
   * @returns True while carried.
   */
  public get carried(): boolean {
    return this.pending.carried;
  }

  /**
   * Where the pending move would land.
   *
   * @returns The destination rectangle, or null while no move is pending.
   */
  public get destination(): AreaRect | null {
    return this.pending.destination;
  }

  /**
   * The centre tile of the block where it would land, or of the selection while no move is pending (decision 53):
   * the tile holding the rectangle's middle point.
   *
   * @returns The tile, or null with no selection.
   */
  public get centre(): { readonly x: number; readonly y: number } | null {
    const rect = this.destination ?? this.selected;
    return rect === null ? null : { x: Math.floor((rect.x0 + rect.x1 + 1) / 2), y: Math.floor((rect.y0 + rect.y1 + 1) / 2) };
  }

  /**
   * The map's centre tile (decision 53), which the bar reads beside the block's and the map marks with a cross.
   *
   * @returns The tile.
   */
  public get mapCentre(): { readonly x: number; readonly y: number } {
    const map = this.host.map();
    return { x: Math.floor(map.width / 2), y: Math.floor(map.height / 2) };
  }

  /**
   * Picks the selection up (decision 51): from now `carryTo` moves it, until it is put down.
   *
   * @returns False with nothing selected.
   */
  public pickUp(): boolean {
    return this.pending.pickUp();
  }

  /**
   * Carries a picked-up block so that its centre tile is the one given, held inside the map.
   *
   * @param tile The tile under the pointer; it may lie off the map.
   */
  public carryTo(tile: { readonly x: number; readonly y: number }): void {
    this.pending.carryTo(tile);
  }

  /**
   * Puts the block down where it is carried, still pending.
   */
  public putDown(): void {
    this.pending.putDown();
  }

  /**
   * Sets the pending offset from a drag, held so the destination stays inside the map (decision 24).
   *
   * @param offset The offset in tiles.
   */
  public dragTo(offset: AreaOffset): void {
    this.pending.dragTo(offset);
  }

  /**
   * Sets the pending offset as typed, not held to the map: one that would leave it is refused by its plan and
   * shown (decision 24).
   *
   * @param offset The offset in tiles.
   * @returns False when the offset is not two whole numbers and was refused, or nothing is selected.
   */
  public setOffset(offset: AreaOffset): boolean {
    return this.pending.setOffset(offset);
  }

  /**
   * Steps the pending block by whole tiles (decision 17), from where it stands; a carried block is put down first.
   *
   * @param dx Tiles right.
   * @param dy Tiles down.
   */
  public nudge(dx: number, dy: number): void {
    const from = this.pending.offset ?? { dx: 0, dy: 0 };
    this.pending.setOffset({ dx: from.dx + dx, dy: from.dy + dy });
  }

  /**
   * Puts a pending move back (decision 16): the selection stays where it is.
   */
  public cancelMove(): void {
    if (this.endPending()) {
      this.host.notify();
    }
  }

  /**
   * The planner's plan for the pending move against the document and the locks as they stand.
   *
   * @returns The plan, or null while no move is pending.
   */
  public movePlan(): AreaMovePlan | null {
    const rect = this.selected;
    const offset = this.pending.offset;
    if (rect === null || offset === null || this.pasteBase !== null) {
      return null;
    }
    const world = this.host.document();
    const kept = this.keptPlan;
    if (kept !== null && kept.world === world && kept.rect === rect && kept.offset === offset) {
      return kept.plan;
    }
    const plan = this.plans.movePlan(world, this.host.mapId(), rect, offset, this.layers().taken);
    this.keptPlan = { world, rect, offset, plan };
    return plan;
  }

  /**
   * The paint the pending move or paste replaces as boxes in tiles (`areaReplacedBoxes`), for the map's tint.
   *
   * @returns The boxes; none unless the move or paste is over paint.
   */
  public replacedBoxes(): readonly AreaTileBox[] {
    const plan = this.pendingPlan();
    return plan === null ? [] : this.plans.replacedBoxes(this.host.map(), plan);
  }

  /**
   * Confirms the pending move (decisions 16, 28 and 30): plans it against the document as it stands now and runs
   * the one compound; the selection then stands at the destination with nothing pending. A refused move stays
   * pending; one that changes nothing ends (decision 29).
   *
   * @returns What it did.
   */
  public move(): AreaMoveOutcome {
    const rect = this.selected;
    const offset = this.pending.offset;
    const plan = this.movePlan();
    if (rect === null || offset === null || plan === null) {
      return { kind: "nothing" };
    }
    if (plan.kind === "nothing") {
      this.endPending();
      this.host.notify();
      return { kind: "nothing", still: offset.dx === 0 && offset.dy === 0 };
    }
    const outcome = moveArea(this.host, plan, offset);
    if (outcome.kind === "moved" && plan.kind === "move") {
      const to = shiftedRect(rect, offset);
      this.lastMove = { command: plan.command, from: rect, to };
      this.selected = to;
      this.endPending();
      this.host.notify();
    }
    return outcome;
  }

  /**
   * The area's clipboard (decision 37), or null before anything was copied or cut: plain data of what the layers the
   * area took held, which stays until the next copy or cut and survives a paste, so one copy pastes again.
   *
   * @returns The clipboard.
   */
  public get clipboard(): AreaClipboard | null {
    return this.held;
  }

  /**
   * What pasting the clipboard makes new (decision 38): props with a behaviour, arrival points and the door cells the
   * copy left out.
   *
   * @returns The counts, or null before anything was copied.
   */
  public get clipboardNotes(): AreaClipboardNotes | null {
    return this.held === null ? null : areaClipboardNotes(this.held, this.host.guard().arrivalType);
  }

  /**
   * Whether a paste is pending: the block the offset describes is the clipboard's, not the selection's.
   *
   * @returns True while a paste waits.
   */
  public get pasting(): boolean {
    return this.pasteBase !== null;
  }

  /**
   * The rectangle the pending offset is measured from: where the clipboard was copied while a paste is pending, else
   * the selection.
   *
   * @returns The rectangle, or null with neither.
   */
  public get pendingSource(): AreaRect | null {
    return this.pasteBase ?? this.selected;
  }

  /**
   * Ends the pending move or paste, telling no one: the caller notifies.
   *
   * @returns True when one was pending or carried.
   */
  private endPending(): boolean {
    this.pasteBase = null;
    this.keptPaste = null;
    return this.pending.end();
  }

  /**
   * Copies what the area takes into the clipboard (decision 37, `Ctrl+C`). Runs no command and changes no document.
   * Nothing is selected, the reach is This layer, or the layers in reach hold nothing there: the clipboard stays as
   * it was.
   *
   * @returns The clipboard kept, or nothing with the door cells that were left out.
   */
  public copy(): AreaCopyOutcome {
    const rect = this.selected;
    if (rect === null || this.reachKind !== "all") {
      return { kind: "nothing", doorCells: 0 };
    }
    const clipboard = copyArea(this.host.document(), this.host.mapId(), rect, this.layers().taken);
    if (clipboard === null) {
      return { kind: "nothing", doorCells: areaDoorCells(this.host.map(), rect, this.layers().taken) };
    }
    this.held = clipboard;
    this.host.notify();
    return { kind: "copied", clipboard };
  }

  /**
   * Cuts what the area takes (decision 37, `Ctrl+X`): copy then Delete, the delete one command and one undo. The
   * clipboard is kept only when the delete ran; a delete the planner refuses, a move pending or a store that drops
   * the command cuts nothing and leaves the clipboard as it was.
   *
   * @returns What it did.
   */
  public cut(): AreaCutOutcome {
    const rect = this.selected;
    if (rect === null || this.reachKind !== "all") {
      return { kind: "nothing" };
    }
    if (this.pending.offset !== null) {
      return { kind: "moving" };
    }
    const clipboard = copyArea(this.host.document(), this.host.mapId(), rect, this.layers().taken);
    if (clipboard === null) {
      return { kind: "nothing" };
    }
    const outcome = deleteArea(this.host, rect, this.layers().taken, this.host.guard());
    if (outcome.kind !== "deleted") {
      return outcome;
    }
    this.held = clipboard;
    this.host.notify();
    return { kind: "cut", clipboard, deleted: outcome };
  }

  /**
   * Puts the clipboard down as a pending block (decision 37, `Ctrl+V`): picked up, so it follows the pointer, with
   * the clipboard's rectangle as the place its offset is measured from. A move pending is put back. Nothing in the
   * document changes until `confirmPaste`.
   *
   * @returns "started", or why not.
   */
  public paste(): AreaPasteStart {
    if (this.reachKind !== "all") {
      return "reach";
    }
    const held = this.held;
    if (held === null) {
      return "empty";
    }
    if (held.mapId !== this.host.mapId()) {
      return "other-map";
    }
    this.endPending();
    this.pasteBase = held.origin;
    this.pending.pickUp();
    return "started";
  }

  /**
   * Copies the area and starts pasting the copy (decision 61, `Ctrl+D`).
   *
   * @returns Started, or nothing with the door cells that were left out when there was nothing to copy.
   */
  public duplicate(): AreaDuplicateOutcome {
    const copied = this.copy();
    if (copied.kind === "nothing") {
      return copied;
    }
    return this.paste() === "started" ? { kind: "started" } : { kind: "nothing", doorCells: 0 };
  }

  /**
   * The plan of the pending paste, for the bar and the outline: made against the document and the locks as they
   * stand, with ids that belong to no store, so showing it takes nothing from the allocator. Remembered for a
   * document, the clipboard, an offset and the layers taken.
   *
   * @returns The plan, or null while no paste is pending.
   */
  private pastePlan(): AreaPastePlan | null {
    const held = this.held;
    const offset = this.pending.offset;
    if (this.pasteBase === null || held === null || offset === null) {
      return null;
    }
    const world = this.host.document();
    const taken = this.layers().taken;
    const key = JSON.stringify(taken);
    const kept = this.keptPaste;
    if (kept !== null && kept.world === world && kept.held === held && kept.key === key && kept.offset.dx === offset.dx && kept.offset.dy === offset.dy) {
      return kept.plan;
    }
    const plan = planAreaPaste(world, this.host.mapId(), held, { x: held.origin.x0 + offset.dx, y: held.origin.y0 + offset.dy }, taken, (prefix, count) => Array.from({ length: count }, (_, n) => `pending-${prefix}-${n}`), this.host.guard().types);
    this.keptPaste = { world, held, offset, key, plan };
    return plan;
  }

  /**
   * The plan of what is pending: the paste's while a paste waits, else the move's.
   *
   * @returns The plan, or null while nothing is pending.
   */
  public pendingPlan(): AreaMovePlan | AreaPastePlan | null {
    return this.pasteBase === null ? this.movePlan() : this.pastePlan();
  }

  /**
   * Confirms the pending paste (decisions 37 and 38): plans it against the document as it stands now with the store's
   * allocator, so every pasted prop and marker takes a new id, and runs the one compound; the selection then stands
   * on the block where it landed and the clipboard stays. A refused paste stays pending; one that changes nothing
   * ends.
   *
   * @returns What it did.
   * @throws Error when the planner does; the pending paste is left as it was.
   */
  public confirmPaste(): AreaPasteOutcome {
    const held = this.held;
    const offset = this.pending.offset;
    if (this.pasteBase === null || held === null || offset === null) {
      return { kind: "nothing" };
    }
    const at = { x: held.origin.x0 + offset.dx, y: held.origin.y0 + offset.dy };
    const plan = planAreaPaste(this.host.document(), this.host.mapId(), held, at, this.layers().taken, (prefix, count) => this.host.allocateIds(prefix, count), this.host.guard().types);
    if (plan.kind === "nothing") {
      this.endPending();
      this.host.notify();
      return { kind: "nothing" };
    }
    const outcome = pasteArea(this.host, plan, at);
    if (outcome.kind === "pasted") {
      this.selected = shiftedRect(held.origin, offset);
      this.endPending();
      this.host.notify();
    }
    return outcome;
  }

  /**
   * Follows an undo or a redo of the last confirmed move, so one undo restores what the builder saw: when the
   * command turned is that move's and the selection still stands where the move left it, the selection goes back
   * to the source on an undo and to the destination again on a redo. Tells no one: the store notifies after a
   * turn.
   *
   * @param command The command undone or redone.
   * @param redo Whether it was redone.
   */
  public turned(command: Command, redo: boolean): void {
    if (this.lastMove !== null && this.lastMove.command === command) {
      this.follow(redo);
    }
  }

  /**
   * Follows an undo or a redo made through a live session, which names no command: the selection follows when the
   * first thing the last move changed, a prop, a marker or, for a move that carried only cells, a cell, now holds
   * what that move's undo, or its redo, leaves there. Tells no one: the store notifies when it moved.
   *
   * @param redo Whether it was a redo.
   * @returns True when the selection moved.
   */
  public turnedLive(redo: boolean): boolean {
    const last = this.lastMove;
    if (last === null || last.command.kind !== "compound") {
      return false;
    }
    const parts = last.command.commands;
    const witness = parts.find((part) => part.kind === "updateProp" || part.kind === "updateEntity") ?? parts[0];
    return witness !== undefined && commandStands(this.host.document(), this.host.map(), witness, redo) && this.follow(redo);
  }

  /**
   * Puts the selection on the last move's source after its undo, or on its destination after its redo, when the
   * selection still stands where that move left it and nothing is pending.
   *
   * @param redo Whether the move was redone.
   * @returns True when the selection moved.
   */
  private follow(redo: boolean): boolean {
    const last = this.lastMove;
    if (last === null || this.pending.offset !== null || !sameAreaRect(this.selected, redo ? last.from : last.to)) {
      return false;
    }
    this.selected = redo ? last.to : last.from;
    return true;
  }
}
